import { useQueueAppContext } from '@repo/ccc-queue-app-lib/src/lib/QueueAppContext/useQueueAppContext.ts'
import { useIdentifyAgent } from '@repo/internal-tools-analytics/src/useIdentifyAgent.ts'
import { useAgentAuthInfoQuery } from '@repo/internal-tools-auth/src/services/AuthInfoApiService/useAgentAuthInfoQuery.ts'
import { DatadogRUM } from '@repo/internal-tools-datadog-rum/src/lib/datadogRum.ts'
import { Loader } from '@repo/internal-tools-design-system/src/lib/Loader/Loader.tsx'
import { PermissionContextProvider } from '@repo/internal-tools-permission/src/context/PermissionContext.tsx'
import { RoleBaseAccessControlService } from '@repo/internal-tools-permission/src/services/RoleBaseAccessControlService.ts'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useMemo } from 'react'
import { AuthenticatedLayout } from '../lib/AuthenticatedLayout.tsx'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayoutRoute,
})

function AuthenticatedLayoutRoute() {
  const { baseApiUrl, authErrorRedirect } = useQueueAppContext()

  const { isAgentAuthInfoLoading, agentAuthInfo, isAgentAuthenticated } =
    useAgentAuthInfoQuery({
      baseApiUrl,
      authErrorRedirect,
      shouldRedirectOnAuthError: true,
      refetchOnMount: true, // Bypass the cache and force fetching.
    })

  useIdentifyAgent({
    // - userName is actually an email.
    // - use userName as ID so it's easier to search and filter in Amplitude.
    agentId: agentAuthInfo?.userName,
    agentEmail: agentAuthInfo?.userName,
  })

  // set unique visitor email for datadog
  if (agentAuthInfo?.userName) {
    DatadogRUM.setUser(agentAuthInfo.userName)
  }

  const roleBaseAccessControlSvc = useMemo(
    () => new RoleBaseAccessControlService(agentAuthInfo?.permissions ?? {}),
    [agentAuthInfo?.permissions],
  )

  // Check `!isAgentAuthenticated` in case the query is loaded
  // but waiting for auto redirect to the login page.
  if (isAgentAuthInfoLoading || !isAgentAuthenticated) {
    return <Loader isFullPage>Logging in...</Loader>
  }

  return (
    <PermissionContextProvider roleBaseAccessService={roleBaseAccessControlSvc}>
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    </PermissionContextProvider>
  )
}
